<script lang="ts">
import { uniqueId, isEmpty, debounce } from 'lodash'
import { format } from 'date-fns'
import { PropType, defineComponent, ComponentPublicInstance } from 'vue'
import { createPopper, Instance as PopperInstance } from '@popperjs/core'
import NrDatepickerCalendar from './nr-datepicker-calendar.vue'
import NrIconButton from '../NrIconButton/nr-icon-button.vue'
import NrFormLabel from '../form-elements/nr-form-label.vue'

interface ValidationProp {
  [key: string]: { message: string; validate(val: string): Promise<boolean> }
}

type ComponentInstance = ComponentPublicInstance & {
  $refs: {
    trigger: ComponentPublicInstance
    calendar: ComponentPublicInstance & {
      $refs: {
        datesEl: (ComponentPublicInstance & { selected: boolean })[]
      }
    }
  }
  $el: HTMLElement
}

const COMPONENT_NAME = 'NrDatepicker'

function formateDateToString(date: Date): string {
  return format(date, 'MM/dd/yyyy')
}

export default defineComponent({
  name: COMPONENT_NAME,
  components: { NrDatepickerCalendar, NrIconButton, NrFormLabel },
  emits: ['update:modelValue', 'on-validate', 'input'],
  props: {
    calendarStyles: {
      type: Object as PropType<Record<string, string>>,
      default: undefined
    },
    hideLabel: { type: Boolean, default: false },
    hideCalendar: { type: Boolean, default: undefined },
    modelValue: {
      type: String,
      default() {
        return ''
      }
    },
    name: { type: String },
    required: { type: Boolean },
    validation: {
      type: Object as PropType<ValidationProp>,
      default() {
        return {}
      }
    },
    minDate: { type: String, default: undefined },
    maxDate: { type: String, default: undefined },
    placeholder: { type: String, default: 'mm/dd/yyyy' },
    disabled: { type: Boolean, default: false },
    unselectableDates: {
      type: Array as PropType<(Date | string)[]>,
      default: undefined
    }
  },
  data() {
    return {
      selected: '',
      show: false,
      errors: {} as { [key: string]: { message: string } },
      popper: null as PopperInstance | null
    }
  },

  created() {
    this.selected = this.modelValue
    this.validate = debounce(this.validate, 300)
    if (this.selected.length) {
      this.validate()
    }
  },

  updated() {
    if (this.popper) {
      this.popper.update()
    }
  },

  mounted() {
    const self = this as unknown as ComponentInstance
    const trigger = self.$refs.trigger
    const calendar = self.$refs.calendar

    if (trigger && calendar) {
      this.popper = createPopper(trigger.$el, calendar.$el, {
        strategy: 'absolute',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 16]
            }
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: self.$el
            }
          }
        ]
      })

      this.popper.update()
    }
  },

  beforeDestroy() {
    if (this.popper) {
      this.popper.destroy()
    }
  },

  methods: {
    handleInput(event: Event) {
      const { value: val } = event.target as HTMLInputElement
      this.selected = val
      this.validate()
      this.$emit('input', this.selected)
      this.$emit('update:modelValue', this.selected)
    },

    handeInputKeypress(event: KeyboardEvent) {
      this.validate()
      switch (event.key) {
        case 'Enter':
          this.handleInput(event)
          this.toggleShow(false)
          event.preventDefault()
          break
      }
    },

    handleSelect(date: Date) {
      this.selected = formateDateToString(date)
      this.validate()
      this.$emit('input', this.selected)
      this.$emit('update:modelValue', this.selected)
    },

    toggleShow(state?: boolean) {
      if (typeof state === 'boolean') {
        this.show = state
      } else {
        this.show = !this.show
      }
    },

    validate() {
      Object.keys(this.computedValidations || {}).forEach(async (key) => {
        try {
          if ((this.selected.toString() || '').trim() || this.required) {
            const isValid = await this.computedValidations[key].validate(
              this.selected.toString()
            )
            if (isValid) {
              delete this.errors[key]
              // This lets Vue that the object has changed.
              this.errors = Object.assign({}, this.errors)
            } else {
              throw new Error(`${key} is not valid.`)
            }
          }
          this.$emit('on-validate', true)
        } catch (_error) {
          this.$emit('on-validate', false)
          this.errors = Object.assign({}, this.errors, {
            [`${key}`]: this.computedValidations[key].message
          })
        }
      })
    },

    convertStringToDate(string: string): Date {
      const date = new Date(string)

      if (isNaN(date.getTime())) {
        const date = new Date()
        return new Date(date.getFullYear(), date.getMonth(), date.getDate())
      } else {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate())
      }
    }
  },
  computed: {
    date(): string {
      return this.selected ?? this.modelValue
    },

    id(): string {
      return uniqueId(`${COMPONENT_NAME}-`)
    },

    formattedDate(): string {
      return this.selected
    },

    isVisibile(): boolean {
      if (this.hideCalendar === undefined || this.hideCalendar === null) {
        return this.show
      } else {
        return !this.hideCalendar
      }
    },

    computedValidations(): {
      [key: string]: {
        message: string
        validate(val: string): Promise<boolean>
      }
    } {
      const valids: {
        [key: string]: {
          message: string
          validate(val: string): Promise<boolean>
        }
      } = {}

      if (this.required) {
        Object.assign(valids, this.validation, {
          required: {
            message: 'This field is required.',
            validate(val: string) {
              return Promise.resolve(!!(val || '').trim())
            }
          }
        })
      }
      return Object.assign(valids, this.validation, {
        validDateFormat: {
          message: 'Must use date format mm/dd/yyyy',
          validate(val: string) {
            return Promise.resolve(/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(val))
          }
        },
        validDate: {
          message: 'Must be a valid date',
          validate(val: string) {
            // check for correct format also to prevent duplicate errors
            return Promise.resolve(
              !/^\d{1,2}\/\d{1,2}\/\d{1,4}$/.test(val) ||
                !isNaN(new Date(val).getTime())
            )
          }
        }
      })
    },

    inputClassnames(): (string | { [key: string]: boolean })[] {
      return ['input', { 'is-danger': !isEmpty(this.errors) }]
    },

    computedMinDate(): Date | undefined {
      return this.minDate ? new Date(this.minDate) : undefined
    },

    computedMaxDate(): Date | undefined {
      return this.maxDate ? new Date(this.maxDate) : undefined
    },

    computedUnselectableDates(): number[] {
      if (!this.unselectableDates) return []
      return this.unselectableDates
        .map((date) => {
          try {
            return Date.parse(format(new Date(date), 'MM/dd/yyyy'))
          } catch (error) {
            return null
          }
        })
        .filter((date): date is number => date !== null)
    }
  },

  watch: {
    show(next: boolean) {
      const self = this as unknown as ComponentInstance
      if (next) {
        const calendar = self.$refs.calendar
        const datesEl = calendar.$refs.datesEl
        const selectedDateEl = datesEl.find((el) => el.selected)

        this.$nextTick(() => {
          ;(selectedDateEl?.$el as HTMLButtonElement)?.focus()
        })
      } else {
        this.$nextTick(() => {
          self.$refs.trigger.$el.focus()
        })
      }
    },
    modelValue(next: string) {
      if (next || next === '') {
        this.selected = next
        return
      }

      this.selected = ''
    }
  }
})
</script>

<template>
  <div class="nr-datepicker field">
    <NrFormLabel
      :class="['nr-label', { 'is-sr-only': hideLabel }]"
      :id="`${id}__label`"
      :for="id"
    >
      <template v-if="required">*</template>
      <slot name="label">NextRequest datepicker</slot>
    </NrFormLabel>

    <div class="field is-grouped">
      <div class="control is-expanded">
        <input
          aria-autocomplete="none"
          type="text"
          :placeholder="placeholder"
          :name="name"
          :aria-labelledby="`${id}__label`"
          :class="inputClassnames"
          :id="id"
          :value="formattedDate"
          :disabled="disabled"
          @input="handleInput"
          @keypress="handeInputKeypress"
          @blur="validate"
        />
      </div>

      <div class="control">
        <NrIconButton
          ref="trigger"
          @click="toggleShow"
          variant="bordered-neutral"
          size="large"
          :icon="{ name: 'calendar-alt', type: 'regular' }"
          :aria-label="`Select a date, currently selected date is ${new Date(
            formattedDate
          ).toDateString()}`"
          :disabled="disabled"
          type="button"
        />
      </div>
    </div>

    <p v-for="(error, name) in errors" :key="name" class="help is-danger">
      {{ error }}
    </p>

    <NrDatepickerCalendar
      @hide="toggleShow(false)"
      @selected="handleSelect"
      :date="convertStringToDate(formattedDate)"
      :minDate="computedMinDate"
      :maxDate="computedMaxDate"
      :unselectables="computedUnselectableDates"
      :style="calendarStyles"
      v-show="isVisibile"
      ref="calendar"
    />
  </div>
</template>

<style lang="scss">
.nr-datepicker {
  .nr-datepicker-calendar-dates.is-narrow th,
  .nr-datepicker-calendar-dates.is-narrow td {
    padding: 0;
    border: none;
  }

  .nr-datepicker-day {
    font-size: 0.875rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    text-decoration: none;
  }
}
</style>
